import React, { useContext } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../api_config/firebase-configuration";
import { Box, Container, Typography } from "@mui/material";
import { NextSeo } from "next-seo";
import { jobBoardSort } from "../src/lib/global2";
import { useUserData } from "../src/hooks/useUserData";
import { ContentProvider } from "../services/content/contentProvider";
import { LocalizationContext } from "../src/lib/context";
import { GetStaticProps, GetStaticPropsContext } from "next";
import DiscountPageHeader from "src/components/Discounts/DiscountsPageHeader";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import PromotionalCard from "src/components/CommonComponents/PromotionalCard";
import JobCategories from "src/components/JobCategories";
import BottomInfo from "src/components/BottomInfo";
import { keyBy, merge, values } from "lodash";
import InspirationsForm from "src/components/Inspirations/InspirationForm/InspirationsForm";
import JobsSearch from "../src/components/Jobs/JobsSearch";
import { useGoogleMapScript } from "../src/hooks/useGoogleMapScript";
import { ViewInArOutlined } from "@mui/icons-material";

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const tendersQueryName = "allTenders";
  const jobsContentProvider = new ContentProvider("jobs");
  const jobsQuery = jobsContentProvider.generateQuery(
    tendersQueryName,
    [
      {
        fields: [
          "id",
          "title",
          "projectType",
          "country",
          "place",
          "skills",
          "exampleImages {url}",
          "offerStart",
          "offerDeadline",
          "uploadDeadline",
        ],
      },
    ],
    {
      locale: context.locale,
      orderBy: "offerDeadline_ASC",
      fallbackLocales: context.defaultLocale,
      first: 100,
    }
  );
  let jobs = await jobsContentProvider.request(jobsQuery, tendersQueryName);
  if (jobs === undefined) {
    jobs = null;
  }
  return {
    props: { jobs },
    revalidate: 7200,
  };
};

const featuresData = [
  {
    skillId: "creative",
    image: <ColorLensOutlinedIcon fontSize="large" />,
  },
  {
    skillId: "service",
    image: <HandshakeOutlinedIcon fontSize="large" />,
  },
  {
    skillId: "survey and mapping",
    image: <ArchitectureOutlinedIcon fontSize="large" />,
  },
  {
    skillId: "agricultural",
    image: <AgricultureIcon fontSize="large" />,
  },
  {
    skillId: "FPV",
    image: <ViewInArOutlined fontSize="large" />,
  },
];

export default function Jobs({ jobs }: { jobs: any }) {
  useGoogleMapScript();
  const { jobPage, jobsPage, uiGeneral, errorPage } =
    useContext(LocalizationContext).localeStrings;

  const joinPromoStrings = uiGeneral.promoCards.filter(
    (c) => c.promoId === "join"
  )[0];
  const discountsPromoStrings = uiGeneral.promoCards.filter(
    (c) => c.promoId === "discounts"
  )[0];

  const { user } = useUserData();
  const offerDeadlineText = jobPage.offerDeadline;

  const localizedFeatures = uiGeneral.skills.filter(
    (s) => s.skillId !== "education" && s.skillId !== "other"
  );
  const mergedFeatures = values(
    merge(keyBy(localizedFeatures, "skillId"), keyBy(featuresData, "skillId"))
  );

  const bottomInfoTexts = [
    jobsPage.aboutBody1,
    jobsPage.aboutBody2,
    jobsPage.aboutBody3,
    jobsPage.aboutBody4,
    jobsPage.aboutBody5,
    jobsPage.aboutBody6,
  ];

  return (
    <>
      {/* test sharing*/}
      <NextSeo
        title={jobsPage.title}
        description={jobsPage.description}
        openGraph={{
          type: "website",
          url: "https://www.dronecakes.com/jobs",
          title: jobsPage.title,
          description: jobsPage.description,
          images: [
            {
              url: "/assets/index_thumbnail.webp",
              alt: jobsPage.title,
            },
          ],
        }}
        twitter={{
          handle: "@handle",
          site: "@site",
          cardType: "summary_large_image",
        }}
      />
      <div className="discounts mb-large">
        <DiscountPageHeader
          subTitle={jobsPage.pageTitle}
          title={jobsPage.pageSubtitle}
          image="/assets/jobs_cover_img.jpeg"
        />
        <Container className="discounts__info">
          <h3>
            {
              uiGeneral.promoCards.filter((f) => f.promoId === "jobs")[0]
                .description
            }
          </h3>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                sm: "repeat(auto-fill, minmax(450px, 1fr))",
                xs: "1fr",
              },
              gap: 4,
              margin: "40px 0",
            }}
          >
            {mergedFeatures.map((feature) => (
              <Box
                key={feature.skillId}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Box
                  className="icon gap-normal"
                  sx={{
                    display: { md: "block", xs: "flex" },
                    marginBottom: { md: 0, xs: "22px" },
                  }}
                >
                  {feature.image}
                  <Typography variant="h2">{feature.name}</Typography>
                </Box>
                <Typography>{feature.description}</Typography>
              </Box>
            ))}
          </Box>

          <div className="discounts__info--divider" />
        </Container>

        <JobsSearch jobs={jobs} />

        <Container
          sx={{
            maxWidth: "1248px !important",
          }}
        >
          <PromotionalCard
            title={discountsPromoStrings.title}
            content={discountsPromoStrings.description}
            imgUrl="/assets/discounts_cover_img.webp"
            btnContent={discountsPromoStrings.buttonText}
            link="/discounts"
            left
            dark
          />
        </Container>

        <JobCategories jobs={jobs} offerDeadlineText={offerDeadlineText} />

        <Container
          sx={{
            maxWidth: "1248px !important",
          }}
        >
          {!user && (
            <PromotionalCard
              title={joinPromoStrings.title}
              content={joinPromoStrings.description}
              imgUrl="/assets/community-2.webp"
              btnContent={joinPromoStrings.buttonText}
              link="/register"
              dark
            />
          )}

          <Box sx={{ mx: "auto", mt: 6 }}>
            <InspirationsForm />
          </Box>
        </Container>
        <BottomInfo
          headerText={jobsPage.aboutTitle}
          textsArray={bottomInfoTexts}
          sendEmail={true}
          email={errorPage?.mailToLink}
        />
      </div>
    </>
  );
}
